import { activeMeasurementIdAtom, measurementsAtom, StoredMeasurement } from '@/atoms/measurements.atom';
import { Text } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { useAtomValue } from 'jotai';
import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import PointX from './PointX';

const CylinderLine = ({
  start,
  end,
  color,
  opacity = 0.8,
  radius = 0.005,
}: {
  start: THREE.Vector3;
  end: THREE.Vector3;
  color: string;
  opacity?: number;
  radius?: number;
}) => {
  const { midpoint, length, quaternion } = useMemo(() => {
    const mid = start.clone().add(end).multiplyScalar(0.5);
    const len = start.distanceTo(end);
    const dir = new THREE.Vector3().subVectors(end, start).normalize();
    const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
    return { midpoint: mid, length: len, quaternion: quat };
  }, [start, end]);

  if (length < 1e-6) return null;

  return (
    <mesh position={midpoint} quaternion={quaternion} renderOrder={Number.MAX_SAFE_INTEGER - 100}>
      <cylinderGeometry args={[radius, radius, length, 8, 1]} />
      <meshBasicMaterial color={color} transparent opacity={opacity} depthTest={false} />
    </mesh>
  );
};

const MAX_MARKER_SCALE = 0.5;
const ORTHO_MARKER_SCALE = 0.1;

const calculateScale = (point: THREE.Vector3, camera: THREE.Camera, renderer: THREE.WebGLRenderer) => {
  if (camera.type === 'OrthographicCamera') {
    return ORTHO_MARKER_SCALE;
  }

  const d = camera.position.distanceTo(point);
  const pr = renderer.getPixelRatio();
  const height = renderer.domElement.clientHeight * pr;
  const perspectiveCamera = camera as THREE.PerspectiveCamera;
  const vFOV = (perspectiveCamera.getEffectiveFOV() * Math.PI) / 180;
  const worldScreenHeight = 2 * d * Math.tan(vFOV / 2);
  const s = (20 * worldScreenHeight) / height;
  return Math.min(s, MAX_MARKER_SCALE);
};

const ORIGIN = new THREE.Vector3(0, 0, 0);

const SavedDistanceMeasurement = ({ measurement }: { measurement: StoredMeasurement }) => {
  const camera = useThree((s) => s.camera);
  const renderer = useThree((s) => s.gl);

  const points = useMemo(() => measurement.points.map((p) => new THREE.Vector3(...p)), [measurement.points]);
  const textPosition = useMemo(() => points[0].clone().add(points[1]).multiplyScalar(0.5), [points]);
  const distance3D = useMemo(() => points[0].distanceTo(points[1]), [points]);

  const showComponents = !!measurement.showComponents;

  // Component geometry (vertical + horizontal decomposition)
  const components = useMemo(() => {
    const baseZ = Math.min(points[0].z, points[1].z);
    const lowerPoint = points[0].z < points[1].z ? points[0] : points[1];
    const higherPoint = points[0].z < points[1].z ? points[1] : points[0];
    const cornerPoint = new THREE.Vector3(higherPoint.x, higherPoint.y, baseZ);
    const heightDiff = Math.abs(points[1].z - points[0].z);
    const dx = points[1].x - points[0].x;
    const dy = points[1].y - points[0].y;
    const dist2D = Math.sqrt(dx * dx + dy * dy);
    const heightTextPos = cornerPoint.clone().add(higherPoint).multiplyScalar(0.5);
    const dist2DTextPos = lowerPoint.clone().add(cornerPoint).multiplyScalar(0.5);

    return { lowerPoint, cornerPoint, higherPoint, heightDiff, dist2D, heightTextPos, dist2DTextPos };
  }, [points]);

  const showHeightViz = showComponents && components.heightDiff >= 0.01;

  // Correct initial values read from camera at mount
  const initialFontSize = useRef(textPosition.distanceTo(camera.position) / 50);
  const initialQuat = useRef<[number, number, number, number]>([
    camera.quaternion.x,
    camera.quaternion.y,
    camera.quaternion.z,
    camera.quaternion.w,
  ]);
  const initialScales = useRef(points.map((p) => calculateScale(p, camera, renderer)));

  const pointGroupRefs = useRef<(THREE.Group | null)[]>([]);
  const textRef = useRef<any>(null);
  const heightTextRef = useRef<any>(null);
  const dist2DTextRef = useRef<any>(null);

  useFrame(() => {
    const isOrtho = camera.type === 'OrthographicCamera';

    points.forEach((point, i) => {
      const group = pointGroupRefs.current[i];
      if (group) {
        const s = calculateScale(point, camera, renderer);
        group.scale.setScalar(s);
      }
    });

    if (textRef.current) {
      textRef.current.quaternion.copy(camera.quaternion);
      if (!isOrtho) {
        textRef.current.fontSize = textPosition.distanceTo(camera.position) / 50;
      }
    }
    if (heightTextRef.current) {
      heightTextRef.current.quaternion.copy(camera.quaternion);
      if (!isOrtho) {
        heightTextRef.current.fontSize = components.heightTextPos.distanceTo(camera.position) / 50;
      }
    }
    if (dist2DTextRef.current) {
      dist2DTextRef.current.quaternion.copy(camera.quaternion);
      if (!isOrtho) {
        dist2DTextRef.current.fontSize = components.dist2DTextPos.distanceTo(camera.position) / 50;
      }
    }
  });

  const label = measurement.name ? `${measurement.name}\n${distance3D.toFixed(2)} m` : `${distance3D.toFixed(2)} m`;

  return (
    <>
      {points.map((point, i) => (
        <group
          key={i}
          ref={(el) => {
            pointGroupRefs.current[i] = el;
          }}
          position={point.toArray()}
          scale={initialScales.current[i]}>
          <PointX scale={1} position={ORIGIN} />
        </group>
      ))}
      <CylinderLine start={points[0]} end={points[1]} color='red' />

      {showHeightViz && (
        <>
          <CylinderLine start={components.lowerPoint} end={components.cornerPoint} color='blue' opacity={0.5} />
          <CylinderLine start={components.cornerPoint} end={components.higherPoint} color='blue' opacity={0.5} />
          <CylinderLine start={components.cornerPoint} end={components.higherPoint} color='green' />
          <Text
            ref={heightTextRef}
            position={components.heightTextPos.toArray()}
            fontSize={initialFontSize.current}
            quaternion={initialQuat.current}
            color='white'
            outlineColor='black'
            outlineWidth={0.01}
            outlineBlur={0.01}
            strokeColor='green'
            strokeWidth={0.001}
            depthOffset={Number.MIN_SAFE_INTEGER}
            renderOrder={Number.MAX_SAFE_INTEGER}
            anchorX='center'
            anchorY='middle'>
            H – {components.heightDiff.toFixed(2)}
          </Text>
          <Text
            ref={dist2DTextRef}
            position={components.dist2DTextPos.toArray()}
            fontSize={initialFontSize.current}
            quaternion={initialQuat.current}
            color='white'
            outlineColor='black'
            outlineWidth={0.01}
            outlineBlur={0.01}
            strokeColor='blue'
            strokeWidth={0.001}
            depthOffset={Number.MIN_SAFE_INTEGER}
            renderOrder={Number.MAX_SAFE_INTEGER}
            anchorX='center'
            anchorY='middle'>
            2D – {components.dist2D.toFixed(2)}
          </Text>
        </>
      )}

      <Text
        ref={textRef}
        position={textPosition.toArray()}
        fontSize={initialFontSize.current}
        quaternion={initialQuat.current}
        color='white'
        outlineColor='black'
        outlineWidth={0.01}
        outlineBlur={0.01}
        strokeColor='red'
        strokeWidth={0.001}
        depthOffset={Number.MIN_SAFE_INTEGER}
        renderOrder={Number.MAX_SAFE_INTEGER}
        anchorX='center'
        anchorY='middle'
        textAlign='center'>
        {label}
      </Text>
    </>
  );
};

const computePolygonArea = (points: THREE.Vector3[]): number => {
  if (points.length < 3) return 0;
  const sum = new THREE.Vector3();
  const cross = new THREE.Vector3();
  for (let i = 0; i < points.length; i++) {
    cross.crossVectors(points[i], points[(i + 1) % points.length]);
    sum.add(cross);
  }
  return sum.length() * 0.5;
};

const SavedAreaMeasurement = ({ measurement }: { measurement: StoredMeasurement }) => {
  const camera = useThree((s) => s.camera);
  const renderer = useThree((s) => s.gl);

  const points = useMemo(() => measurement.points.map((p) => new THREE.Vector3(...p)), [measurement.points]);
  const area = useMemo(() => computePolygonArea(points), [points]);

  const centroid = useMemo(() => {
    const c = new THREE.Vector3();
    for (const p of points) c.add(p);
    c.divideScalar(points.length);
    return c;
  }, [points]);

  // Max distance from centroid to any vertex — used as the measurement's "extent"
  const extent = useMemo(() => Math.max(...points.map((p) => p.distanceTo(centroid))), [points, centroid]);

  const edges = useMemo(() => {
    const result: { start: THREE.Vector3; end: THREE.Vector3; distance: number; midpoint: THREE.Vector3 }[] = [];
    for (let i = 0; i < points.length; i++) {
      const j = (i + 1) % points.length;
      result.push({
        start: points[i],
        end: points[j],
        distance: points[i].distanceTo(points[j]),
        midpoint: points[i].clone().add(points[j]).multiplyScalar(0.5),
      });
    }
    return result;
  }, [points]);

  const fillGeometry = useMemo(() => {
    if (points.length < 3) return null;
    const triangles: number[][] = [];
    for (let i = 1; i < points.length - 1; i++) {
      triangles.push([0, i, i + 1]);
    }
    const positions = new Float32Array(triangles.length * 3 * 3);
    for (let i = 0; i < triangles.length; i++) {
      const [a, b, c] = triangles[i];
      positions[i * 9 + 0] = points[a].x;
      positions[i * 9 + 1] = points[a].y;
      positions[i * 9 + 2] = points[a].z;
      positions[i * 9 + 3] = points[b].x;
      positions[i * 9 + 4] = points[b].y;
      positions[i * 9 + 5] = points[b].z;
      positions[i * 9 + 6] = points[c].x;
      positions[i * 9 + 7] = points[c].y;
      positions[i * 9 + 8] = points[c].z;
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.computeVertexNormals();
    return geometry;
  }, [points]);

  const initialQuat = useRef<[number, number, number, number]>([
    camera.quaternion.x,
    camera.quaternion.y,
    camera.quaternion.z,
    camera.quaternion.w,
  ]);
  const initialScales = useRef(points.map((p) => calculateScale(p, camera, renderer)));
  const initialEdgeFontSizes = useRef(edges.map((e) => e.midpoint.distanceTo(camera.position) / 50));
  const initialAreaFontSize = useRef((centroid.distanceTo(camera.position) / 50) * 1.2);

  const pointGroupRefs = useRef<(THREE.Group | null)[]>([]);
  const edgeTextRefs = useRef<any[]>([]);
  const areaTextRef = useRef<any>(null);

  useFrame(() => {
    const isOrtho = camera.type === 'OrthographicCamera';
    const camDist = centroid.distanceTo(camera.position);
    const showEdgeLabels = camDist < extent * 5;

    points.forEach((point, i) => {
      const group = pointGroupRefs.current[i];
      if (group) {
        const s = calculateScale(point, camera, renderer);
        group.scale.setScalar(s);
      }
    });

    edges.forEach((edge, i) => {
      const ref = edgeTextRefs.current[i];
      if (ref) {
        ref.visible = showEdgeLabels;
        ref.quaternion.copy(camera.quaternion);
        if (!isOrtho) {
          ref.fontSize = edge.midpoint.distanceTo(camera.position) / 50;
        }
      }
    });

    if (areaTextRef.current) {
      areaTextRef.current.quaternion.copy(camera.quaternion);
      if (!isOrtho) {
        areaTextRef.current.fontSize = (camDist / 50) * 1.2;
      }
    }
  });

  return (
    <>
      {points.map((point, i) => (
        <group
          key={i}
          ref={(el) => {
            pointGroupRefs.current[i] = el;
          }}
          position={point.toArray()}
          scale={initialScales.current[i]}>
          <PointX scale={1} position={ORIGIN} />
        </group>
      ))}
      {edges.map((edge, i) => (
        <CylinderLine key={`edge-${i}`} start={edge.start} end={edge.end} color='orange' />
      ))}
      {fillGeometry && (
        <mesh geometry={fillGeometry} renderOrder={Number.MAX_SAFE_INTEGER - 200}>
          <meshBasicMaterial color='orange' transparent opacity={0.2} depthTest={false} side={THREE.DoubleSide} />
        </mesh>
      )}
      {edges.map((edge, i) => (
        <Text
          key={`label-${i}`}
          ref={(el) => {
            edgeTextRefs.current[i] = el;
          }}
          position={edge.midpoint.toArray()}
          fontSize={initialEdgeFontSizes.current[i]}
          quaternion={initialQuat.current}
          color='white'
          outlineColor='black'
          outlineWidth={0.01}
          outlineBlur={0.01}
          strokeColor='orange'
          strokeWidth={0.001}
          depthOffset={Number.MIN_SAFE_INTEGER}
          renderOrder={Number.MAX_SAFE_INTEGER}
          anchorX='center'
          anchorY='middle'>
          {edge.distance.toFixed(2)} m
        </Text>
      ))}
      <Text
        ref={areaTextRef}
        position={centroid.toArray()}
        fontSize={initialAreaFontSize.current}
        quaternion={initialQuat.current}
        color='white'
        outlineColor='black'
        outlineWidth={0.015}
        outlineBlur={0.015}
        strokeColor='orange'
        strokeWidth={0.002}
        depthOffset={Number.MIN_SAFE_INTEGER}
        renderOrder={Number.MAX_SAFE_INTEGER}
        anchorX='center'
        anchorY='middle'
        textAlign='center'>
        {measurement.name ? `${measurement.name}\n` : ''}
        {area.toFixed(2)} m²
      </Text>
    </>
  );
};

const SavedMeasurements = ({ projectId }: { projectId: string }) => {
  const measurements = useAtomValue(measurementsAtom(projectId));
  const activeMeasurementId = useAtomValue(activeMeasurementIdAtom);
  const enabledMeasurements = measurements.filter((m) => m.enabled && m.id !== activeMeasurementId);

  return (
    <>
      {enabledMeasurements.map((m) =>
        m.type === 'distance' ? (
          <SavedDistanceMeasurement key={m.id} measurement={m} />
        ) : (
          <SavedAreaMeasurement key={m.id} measurement={m} />
        ),
      )}
    </>
  );
};

export default SavedMeasurements;
